import React from 'react';
import { graphql, useFragment } from 'react-relay';
import { NavigationAnalyticsContext } from '@atlaskit/analytics-namespaced-context';
import { useAccountLinks } from '@atlassian/account';
import { useIsAnonymous } from '@atlassian/jira-tenant-context-controller/src/components/is-anonymous/index.tsx';
import { testIdGenerate } from '@atlassian/jira-navigation-apps-common/src/utils/test-id.tsx';
import {
	getIdentityEnv,
	getSiteUrl,
	getWindow,
} from '@atlassian/jira-atlassian-navigation-nav4-common/src/common/utils.tsx';
import { useEnvironment } from '@atlassian/jira-tenant-context-controller/src/components/environment/index.tsx';
import {
	ANALYTICS_CONTEXT,
	AccountMenuIntlProvider,
	AccountMenuLoggedOutState,
} from '@atlassian/account-menu';
import type { Nav4ProfileNew$key } from '@atlassian/jira-relay/src/__generated__/Nav4ProfileNew.graphql';
import { Nav4AccountMenu } from './account-menu/index.tsx';

type Nav4ProfileNewProps = {
	queryReference: Nav4ProfileNew$key;
};

export function Nav4ProfileNew({ queryReference }: Nav4ProfileNewProps) {
	const isAnonymous = useIsAnonymous();

	return (
		<NavigationAnalyticsContext data={ANALYTICS_CONTEXT}>
			<AccountMenuIntlProvider>
				{isAnonymous ? (
					<Nav4ProfileAnonymous />
				) : (
					<Nav4ProfileLoggedIn queryReference={queryReference} />
				)}
			</AccountMenuIntlProvider>
		</NavigationAnalyticsContext>
	);
}

function Nav4ProfileLoggedIn({ queryReference }: Nav4ProfileNewProps) {
	const data = useFragment<Nav4ProfileNew$key>(
		graphql`
			fragment Nav4ProfileNew on Query {
				me {
					user {
						picture
						... on AtlassianAccountUser {
							email
						}
					}
				}
			}
		`,
		queryReference,
	);

	return (
		<Nav4AccountMenu
			testId={testIdGenerate('secondary-actions', 'profile')}
			picture={data?.me?.user?.picture}
			email={data?.me?.user?.email ?? ''}
		/>
	);
}

function Nav4ProfileAnonymous() {
	const environment = useEnvironment();
	const identityEnv = getIdentityEnv(environment);

	const currentWindow = getWindow();
	const siteUrl = getSiteUrl(currentWindow);

	const links = useAccountLinks({
		email: '',
		identityEnvironment: identityEnv,
		application: 'jira',
		continueUrl: siteUrl,
		isLoggedOut: true,
	});

	return (
		links?.logInLink && (
			<AccountMenuLoggedOutState
				testId={testIdGenerate('secondary-actions', 'profile')}
				logInLink={links.logInLink}
			/>
		)
	);
}
